import type { Dispatch, SetStateAction } from "react";
import type { SiteContent } from "../../home/shared/types/siteContent";
import { syncChallengeEvent } from "../../challenges/services/challengeAdminService";
import type {
  ChallengeDetail,
  ChallengeType,
} from "../../challenges/types/challengeAdmin";
import type {
  ChallengeEventDraft,
  PairMode,
} from "../../challenges/types/challengeDraft";
import type { AdminEventChallengeLink } from "../services/eventRegistrationAdminService";
import type { TournamentRegistrationCategoryItem } from "./tournamentCategories";
import { normalizeTournamentShirtSizes } from "./tournamentCategories";
import {
  isFixedPairOnlyChallengeType,
  parseAmountToCents,
  resolveChallengeEventId,
} from "./adminChallengeEditor";
import { fromDateTimeLocal } from "./tournamentOperationSchedule";

type SyncSavedChallengeEventInput = {
  autoCreateMissingRegistrationsAndCharges: (input: {
    eventId: string;
    challengeType: ChallengeType;
  }) => Promise<void>;
  challengeEventDraft: ChallengeEventDraft;
  challengeTournamentCategories: TournamentRegistrationCategoryItem[];
  currentPairMode: PairMode;
  normalized: ChallengeDetail;
  setContent: Dispatch<SetStateAction<SiteContent>>;
  setEventChallengeLinkDrafts: Dispatch<SetStateAction<Record<string, string>>>;
  setEventChallengeLinks: Dispatch<
    SetStateAction<Record<string, AdminEventChallengeLink>>
  >;
  token: string;
};

function inferRegistrationType({
  challengeTournamentCategories,
  currentPairMode,
  normalized,
}: {
  challengeTournamentCategories: TournamentRegistrationCategoryItem[];
  currentPairMode: PairMode;
  normalized: ChallengeDetail;
}): "individual" | "fixed_pair" {
  const challengeType = String(normalized.challengeType || "")
    .trim()
    .toLowerCase();
  if (challengeType === "super8") return "individual";
  if (challengeType === "tournament") {
    return challengeTournamentCategories.every(
      (category) => category.registrationType === "fixed_pair",
    )
      ? "fixed_pair"
      : "individual";
  }
  if (isFixedPairOnlyChallengeType(normalized.challengeType)) {
    return "fixed_pair";
  }
  return currentPairMode === "fixed" ? "fixed_pair" : "individual";
}

export function normalizeChallengeEventDeadlineForApi(value?: string | null) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) {
    return fromDateTimeLocal(raw) || null;
  }
  return parsed.toISOString();
}

export async function syncSavedChallengeEvent({
  autoCreateMissingRegistrationsAndCharges,
  challengeEventDraft,
  challengeTournamentCategories,
  currentPairMode,
  normalized,
  setContent,
  setEventChallengeLinkDrafts,
  setEventChallengeLinks,
  token,
}: SyncSavedChallengeEventInput) {
  const amountPerPlayerCents =
    parseAmountToCents(challengeEventDraft.amountPerPlayer || "") ??
    (Number.isFinite(Number(challengeEventDraft.savedAmountPerPlayerCents))
      ? Number(challengeEventDraft.savedAmountPerPlayerCents)
      : undefined);
  const eventSyncResult = await syncChallengeEvent(token, normalized.id, {
    enabled: true,
    imageUrl: String(challengeEventDraft.imageUrl || "").trim(),
    amountPerPlayerCents,
    registrationType: inferRegistrationType({
      challengeTournamentCategories,
      currentPairMode,
      normalized,
    }),
    registrationsOpen: Boolean(challengeEventDraft.registrationsOpen),
    shirtSizesEnabled: Boolean(challengeEventDraft.shirtSizesEnabled),
    shirtSizes: challengeEventDraft.shirtSizesEnabled
      ? normalizeTournamentShirtSizes(challengeEventDraft.shirtSizes)
      : [],
    shirtAvailabilityType: challengeEventDraft.shirtSizesEnabled
      ? challengeEventDraft.shirtAvailabilityType
      : "none",
    shirtDeadlineAt:
      challengeEventDraft.shirtSizesEnabled &&
      challengeEventDraft.shirtAvailabilityType === "period"
        ? normalizeChallengeEventDeadlineForApi(
            challengeEventDraft.shirtDeadlineAt,
          )
        : null,
    shirtQuantityLimit:
      challengeEventDraft.shirtSizesEnabled &&
      challengeEventDraft.shirtAvailabilityType === "quantity"
        ? Math.floor(Number(challengeEventDraft.shirtQuantityLimit))
        : null,
  });
  const eventId = String(
    eventSyncResult.eventId || resolveChallengeEventId(normalized),
  ).trim();
  if (
    eventSyncResult.enabled &&
    eventId &&
    Boolean(challengeEventDraft.registrationsOpen)
  ) {
    await autoCreateMissingRegistrationsAndCharges({
      eventId,
      challengeType: normalized.challengeType,
    });
  }
  if (!eventId) return;

  setContent((prev) => {
    const currentEvents = Array.isArray(prev.events) ? prev.events : [];
    const existingEvent = currentEvents.find(
      (item) => String(item?.id || "").trim() === eventId,
    );
    const eventsWithoutCurrent = currentEvents.filter(
      (item) => String(item?.id || "").trim() !== eventId,
    );
    if (!eventSyncResult.enabled) {
      return { ...prev, events: eventsWithoutCurrent };
    }
    return {
      ...prev,
      events: eventsWithoutCurrent.concat({
        id: eventId,
        title:
          String(normalized.challengeName || "").trim() ||
          String(existingEvent?.title || "").trim() ||
          `Desafio #${normalized.id}`,
        date: String(existingEvent?.date || "").trim(),
        address:
          String(normalized.address || "").trim() ||
          String(existingEvent?.address || "").trim(),
        description:
          String(normalized.description || "").trim() ||
          String(existingEvent?.description || "").trim(),
        details:
          String(normalized.description || "").trim() ||
          String(existingEvent?.details || "").trim(),
        imageUrl:
          String(challengeEventDraft.imageUrl || "").trim() ||
          String(existingEvent?.imageUrl || "").trim(),
        amountPerPlayerCents:
          typeof eventSyncResult.amountPerPlayerCents === "number"
            ? eventSyncResult.amountPerPlayerCents
            : Number.isFinite(Number(existingEvent?.amountPerPlayerCents))
              ? Number(existingEvent?.amountPerPlayerCents)
              : null,
        registrationType:
          String(eventSyncResult.registrationType || "")
            .trim()
            .toLowerCase() === "fixed_pair"
            ? "fixed_pair"
            : String(existingEvent?.registrationType || "")
                  .trim()
                  .toLowerCase() === "fixed_pair"
              ? "fixed_pair"
              : "individual",
      }),
    };
  });

  if (eventSyncResult.enabled) {
    setEventChallengeLinks((prev) => ({
      ...prev,
      [eventId]: {
        eventId,
        tournamentId: normalized.id,
        challengeType: normalized.challengeType,
        challengeName: normalized.challengeName,
        scheduledAt: normalized.scheduledAt || null,
        updatedAt: new Date().toISOString(),
      },
    }));
    setEventChallengeLinkDrafts((prev) => ({
      ...prev,
      [eventId]: String(normalized.id),
    }));
    return;
  }

  setEventChallengeLinks((prev) => {
    const next = { ...prev };
    delete next[eventId];
    return next;
  });
  setEventChallengeLinkDrafts((prev) => {
    const next = { ...prev };
    delete next[eventId];
    return next;
  });
}
